"use client";

import {
  type Ref,
  useState,
  type MouseEvent,
  type FormEventHandler,
  useEffect,
} from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
// import {
//     SignInButton,
//     SignUpButton,
//     SignedIn,
//     SignedOut,
//     UserButton,
// } from '@clerk/nextjs'
import { UserButton } from "@daveyplate/better-auth-ui";
import { TbAward, TbHeart } from "react-icons/tb";
import { GrUserAdmin } from "react-icons/gr";
import { SiLichess } from "react-icons/si";
import { useUserInfo } from "./user-info-provider";
import { authClient } from "~/utils/auth-client";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { formatRating } from "./utils";

export interface AuthHeaderProps {
  userRating?: Ref<number>;
}

export function AuthHeader({}: AuthHeaderProps) {
  const { userInfo } = useUserInfo();
  const { data: session, isPending, error } = authClient.useSession();
  const pathname = usePathname();
  const canChangeLichessPassword =
    userInfo?.role === "admin" && pathname?.startsWith("/admin");

  // State for reset rating dialog
  const [openReset, setOpenReset] = useState(false);
  const [login, setLogin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [msgVariant, setMsgVariant] = useState<"error" | "info" | "success">(
    "error",
  );
  // State for set life rating dialog
  const [openSetLife, setOpenSetLife] = useState(false);
  const [loginLife, setLoginLife] = useState("");
  const [lifeRatingVal, setLifeRatingVal] = useState<string | number>("");
  const [submittingLife, setSubmittingLife] = useState(false);
  const [msgLife, setMsgLife] = useState<string | null>(null);
  const [msgLifeVariant, setMsgLifeVariant] = useState<
    "error" | "info" | "success"
  >("error");
  const [openLichessPassword, setOpenLichessPassword] = useState(false);
  const [lichessPassword, setLichessPassword] = useState("");
  const [lichessPasswordConfirm, setLichessPasswordConfirm] = useState("");
  const [lichessPasswordSubmitting, setLichessPasswordSubmitting] =
    useState(false);
  const [lichessPasswordMsg, setLichessPasswordMsg] = useState<string | null>(
    null,
  );
  const isAnyAdminDialogOpen =
    openReset || openSetLife || openLichessPassword;

  const handleOpenReset = (e?: MouseEvent) => {
    e?.preventDefault();
    setMsg(null);
    setOpenReset(true);
  };

  const handleOpenSetLife = (e?: MouseEvent) => {
    e?.preventDefault();
    setMsgLife(null);
    setOpenSetLife(true);
  };

  const handleOpenLichessPassword = (e?: MouseEvent) => {
    e?.preventDefault();
    setLichessPasswordMsg(null);
    setLichessPassword("");
    setLichessPasswordConfirm("");
    setOpenLichessPassword(true);
  };

  const closeLichessPasswordDialog = () => {
    setOpenLichessPassword(false);
    setLichessPassword("");
    setLichessPasswordConfirm("");
    setLichessPasswordMsg(null);
    setLichessPasswordSubmitting(false);
  };

  // Use a non-async handler (rule: no-misused-promises); run async flow inside a voided IIFE
  const handleSubmitReset: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    void (async () => {
      if (!login.trim()) {
        setMsg("Логин обязателен");
        setMsgVariant("error");
        return;
      }
      try {
        setSubmitting(true);
        setMsg(null);
        const resp = await fetch("/api/users/reset-rating", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ login }),
        });

        // Safely parse and narrow the response
        type ResetRatingResponse = { success: boolean; message?: string };
        const isResetResp = (val: unknown): val is ResetRatingResponse => {
          return (
            typeof val === "object" &&
            val !== null &&
            typeof (val as Record<string, unknown>).success === "boolean"
          );
        };

        let dataUnknown: unknown = null;
        try {
          dataUnknown = await resp.json();
        } catch {
          // ignore JSON parse errors; treat as unsuccessful
        }

        if (!resp.ok || !isResetResp(dataUnknown) || !dataUnknown.success) {
          const fallback = "Не удалось сбросить рейтинг";
          const message: string =
            isResetResp(dataUnknown) && typeof dataUnknown.message === "string"
              ? dataUnknown.message
              : fallback;
          throw new Error(message);
        }

        setOpenReset(false);
        setLogin("");
        setMsg(null);
      } catch (err) {
        const message: string =
          err instanceof Error ? err.message : "Не удалось сбросить рейтинг";
        console.error("Error resetting rating:", message);
        setMsg(message);
        setMsgVariant("error");
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const handleSubmitSetLife: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void (async () => {
      if (!loginLife.trim()) {
        setMsgLife("Логин обязателен");
        setMsgLifeVariant("error");
        return;
      }
      if (lifeRatingVal === "" || lifeRatingVal === null) {
        setMsgLife("Укажите рейтинг");
        setMsgLifeVariant("error");
        return;
      }
      try {
        setSubmittingLife(true);
        setMsgLife(null);
        const resp = await fetch("/api/users/set-life-rating", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            login: loginLife,
            lifeRating: Number(lifeRatingVal),
          }),
        });
        type SetLifeResp = { success: boolean; message?: string };
        const isSetLifeResp = (val: unknown): val is SetLifeResp =>
          typeof val === "object" &&
          val !== null &&
          typeof (val as Record<string, unknown>).success === "boolean";
        let dataUnknown: unknown = null;
        try {
          dataUnknown = await resp.json();
        } catch {
          /* noop */
        }
        if (!resp.ok || !isSetLifeResp(dataUnknown) || !dataUnknown.success) {
          const fallback = "Не удалось установить рейтинг";
          const message: string =
            isSetLifeResp(dataUnknown) &&
            typeof dataUnknown.message === "string"
              ? dataUnknown.message
              : fallback;
          throw new Error(message);
        }
        setOpenSetLife(false);
        setLoginLife("");
        setLifeRatingVal("");
        setMsgLife(null);
      } catch (err) {
        const message: string =
          err instanceof Error ? err.message : "Не удалось установить рейтинг";
        console.error("Error setting life rating:", message);
        setMsgLife(message);
        setMsgLifeVariant("error");
      } finally {
        setSubmittingLife(false);
      }
    })();
  };

  const handleSubmitLichessPassword: FormEventHandler<HTMLFormElement> = (
    e,
  ) => {
    e.preventDefault();

    void (async () => {
      const newPassword = lichessPassword.trim();
      const confirmPassword = lichessPasswordConfirm.trim();

      if (!newPassword) {
        setLichessPasswordMsg("Пароль не может быть пустым");
        return;
      }
      if (newPassword !== confirmPassword) {
        setLichessPasswordMsg("Пароли не совпадают");
        return;
      }

      try {
        setLichessPasswordSubmitting(true);
        setLichessPasswordMsg(null);

        const resp = await fetch("/api/admin/lichess-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: newPassword }),
        });

        type LichessPasswordResponse = {
          success: boolean;
          message?: string;
        };
        const isLichessPasswordResponse = (
          val: unknown,
        ): val is LichessPasswordResponse =>
          typeof val === "object" &&
          val !== null &&
          typeof (val as Record<string, unknown>).success === "boolean";

        let dataUnknown: unknown = null;
        try {
          dataUnknown = await resp.json();
        } catch {
          /* noop */
        }

        if (
          !resp.ok ||
          !isLichessPasswordResponse(dataUnknown) ||
          !dataUnknown.success
        ) {
          const fallback = "Не удалось изменить пароль lichess";
          const message: string =
            isLichessPasswordResponse(dataUnknown) &&
            typeof dataUnknown.message === "string"
              ? dataUnknown.message
              : fallback;
          throw new Error(message);
        }

        closeLichessPasswordDialog();
      } catch (err) {
        const message: string =
          err instanceof Error
            ? err.message
            : "Не удалось изменить пароль lichess";
        console.error("Error setting Lichess password:", message);
        setLichessPasswordMsg(message);
      } finally {
        setLichessPasswordSubmitting(false);
      }
    })();
  };

  // Safety: always restore body pointer-events once all admin dialogs are closed
  useEffect(() => {
    if (!isAnyAdminDialogOpen) {
      try {
        document.body?.style?.removeProperty?.("pointer-events");
      } catch {
        /* noop */
      }
    }

    return () => {
      try {
        document.body?.style?.removeProperty?.("pointer-events");
      } catch {
        /* noop */
      }
    };
  }, [isAnyAdminDialogOpen]);

  if (isPending) {
    return (
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        Загрузка...
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {error && (
        <div className="text-red-500">Ошибка авторизации: {error.message}</div>
      )}
      {!session && (
        <div className="text-red-500">Пожалуйста, войдите в систему</div>
      )}
      {session && !userInfo && (
        <div className="text-red-500">
          Загрузка информации о пользователе...
        </div>
      )}
      {session && userInfo && (
        <div className="container flex h-24 items-center px-4">
          <Link href="/" className="mr-6 mt-4 flex items-center space-x-2">
            <Image
              src="/logo.png"
              alt="Logo"
              className="h-8"
              width={110}
              height={32}
            />
          </Link>

          <div className="absolute right-0 flex items-center gap-2">
            <div className="flex flex-row items-center gap-1">
              {formatRating(userInfo?.rating ?? 0)}
              <TbAward />
            </div>
            <div className="flex flex-row items-center gap-1">
              {formatRating(userInfo?.lifeRating ?? 0)}
              <TbHeart />
            </div>
            <UserButton
              className="bg-transparent text-foreground"
              localization={{ SIGN_OUT: "Выйти" }}
              disableDefaultLinks={true}
              additionalLinks={[
                {
                  label: "Панель админа",
                  icon: <GrUserAdmin />,
                  href: "/admin",
                },
                ...(canChangeLichessPassword
                  ? [
                      {
                        label: (
                          <Label
                            data-testid="admin-open-lichess-password"
                            onClick={handleOpenLichessPassword}
                          >
                            Изменить пароль lichess
                          </Label>
                        ),
                        icon: <SiLichess />,
                        href: "#",
                        signedIn: true,
                      },
                    ]
                  : []),
                {
                  label: (
                    <Label
                      data-testid="admin-open-reset"
                      onClick={handleOpenReset}
                    >
                      Сброс рейтинга
                    </Label>
                  ),
                  icon: <TbAward />,
                  href: "#",
                  signedIn: true,
                },
                {
                  label: (
                    <Label
                      data-testid="admin-open-setlife"
                      onClick={handleOpenSetLife}
                    >
                      Установить рейтинг "в жизни"
                    </Label>
                  ),
                  icon: <TbHeart />,
                  href: "#",
                  signedIn: true,
                },
              ]}
            />
          </div>

          {/* Reset rating dialog */}
          <Dialog
            open={openReset}
            onOpenChange={(v) => {
              setOpenReset(v);
              if (!v) {
                setMsg(null);
                setLogin("");
                setSubmitting(false);
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Сброс рейтинга</DialogTitle>
                <DialogDescription>
                  Введите логин пользователя, чтобы сбросить его рейтинг до
                  значения по умолчанию.
                </DialogDescription>
              </DialogHeader>

              {msg && (
                <div
                  role="alert"
                  className={`${msgVariant === "error" ? "border-red-600 bg-black text-red-300" : msgVariant === "success" ? "border-green-600 bg-green-500 text-green-700" : "border-blue-600 bg-blue-50 text-blue-700"} rounded-md border p-3 text-sm`}
                >
                  {msg}
                </div>
              )}

              <form onSubmit={handleSubmitReset} className="space-y-4">
                <div className="space-y-2">
                  {/* <Label htmlFor="reset-login">Логин</Label> */}
                  <Input
                    id="reset-login"
                    data-testid="reset-login-input"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    placeholder="Логин пользователя"
                    required
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setOpenReset(false)}
                    disabled={submitting}
                  >
                    Отмена
                  </Button>
                  <Button
                    data-testid="reset-submit"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? "Сброс..." : "Сбросить"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          {/* Set life rating dialog */}
          <Dialog
            open={openSetLife}
            onOpenChange={(v) => {
              setOpenSetLife(v);
              if (!v) {
                setMsgLife(null);
                setLoginLife("");
                setLifeRatingVal("");
                setSubmittingLife(false);
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Установить рейтинг "в жизни"</DialogTitle>
                <DialogDescription>
                  Введите логин пользователя и новое значение рейтинга (можно с
                  десятичной частью).
                </DialogDescription>
              </DialogHeader>

              {msgLife && (
                <div
                  role="alert"
                  className={`${msgLifeVariant === "error" ? "border-red-600 bg-black text-red-300" : msgLifeVariant === "success" ? "border-green-600 bg-green-500 text-green-700" : "border-blue-600 bg-blue-50 text-blue-700"} rounded-md border p-3 text-sm`}
                >
                  {msgLife}
                </div>
              )}

              <form onSubmit={handleSubmitSetLife} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    id="setlife-login"
                    data-testid="setlife-login-input"
                    value={loginLife}
                    onChange={(e) => setLoginLife(e.target.value)}
                    placeholder="Логин"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Input
                    id="setlife-rating"
                    data-testid="setlife-rating-input"
                    value={lifeRatingVal}
                    onChange={(e) => setLifeRatingVal(e.target.value)}
                    placeholder="Рейтинг"
                    required
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setOpenSetLife(false)}
                    disabled={submittingLife}
                  >
                    Отмена
                  </Button>
                  <Button
                    data-testid="setlife-submit"
                    type="submit"
                    disabled={submittingLife}
                  >
                    {submittingLife ? "Установка..." : "Установить"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog
            open={openLichessPassword}
            onOpenChange={(open) => {
              if (!open) {
                closeLichessPasswordDialog();
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Изменить пароль lichess</DialogTitle>
                <DialogDescription>
                  Задайте новый пароль для открытия нерешенных задач на
                  lichess.org.
                </DialogDescription>
              </DialogHeader>

              {lichessPasswordMsg && (
                <div
                  role="alert"
                  className="rounded-md border border-red-600 bg-black p-3 text-sm text-red-300"
                >
                  {lichessPasswordMsg}
                </div>
              )}

              <form onSubmit={handleSubmitLichessPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="lichess-password-new">Новый пароль</Label>
                  <Input
                    id="lichess-password-new"
                    data-testid="lichess-password-new-input"
                    value={lichessPassword}
                    onChange={(e) => {
                      setLichessPassword(e.target.value);
                      if (lichessPasswordMsg) {
                        setLichessPasswordMsg(null);
                      }
                    }}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lichess-password-confirm">
                    Подтверждение пароля
                  </Label>
                  <Input
                    id="lichess-password-confirm"
                    data-testid="lichess-password-confirm-input"
                    value={lichessPasswordConfirm}
                    onChange={(e) => {
                      setLichessPasswordConfirm(e.target.value);
                      if (lichessPasswordMsg) {
                        setLichessPasswordMsg(null);
                      }
                    }}
                    autoComplete="new-password"
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={closeLichessPasswordDialog}
                    disabled={lichessPasswordSubmitting}
                  >
                    Отмена
                  </Button>
                  <Button
                    data-testid="lichess-password-submit"
                    type="submit"
                    disabled={lichessPasswordSubmitting}
                  >
                    {lichessPasswordSubmitting ? "Сохранение..." : "Сохранить"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </header>
  );
}
